import { memo, useLayoutEffect, useRef, useState } from "react";
import {
  getQuantityString,
  selectUnitSystem,
  useAppSelector,
  useT,
  type Ingredient,
} from "@reciperoll/core";

// Read-only ingredient list for the recipe view: group headings + an aligned
// quantity column (quantity, then name) — the web equivalent of the RN
// RecipeIngredientsView. Instead of RN's onLayout measurement, it measures the
// rendered quantity spans after layout and applies the widest as a min-width so
// the names line up in a neat column.
export const RecipeIngredientsView = memo(function RecipeIngredientsView({
  ingredients,
  scale = 1,
}: {
  ingredients: Ingredient[];
  scale?: number;
}) {
  const unitSystem = useAppSelector(selectUnitSystem);
  const t = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  // Widest quantity measured across the rows; applied as minWidth so names align.
  const [qtyColWidth, setQtyColWidth] = useState(0);

  // Rows currently showing their substitute instead of the main ingredient.
  // View-local by design: a swap is a per-cook choice, not an edit to the recipe.
  const [swapped, setSwapped] = useState<Record<string, boolean>>({});

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let max = 0;
    el.querySelectorAll<HTMLElement>("[data-qty]").forEach((span) => {
      max = Math.max(max, span.offsetWidth);
    });
    // Only grows (mirrors RN), and settles after one pass so it can't loop.
    setQtyColWidth((prev) => (max > prev ? max : prev));
  }, [ingredients, scale, unitSystem, t, swapped]);

  // Ingredients after a group heading are indented until the next heading.
  let inGroup = false;

  return (
    <div ref={containerRef} className="max-w-xl">
      {ingredients.map((ingredient) => {
        if (ingredient.group) {
          inGroup = !!ingredient.name;
          return (
            <div key={ingredient.id} className={ingredient.name ? "mt-6 mb-2" : "mt-6"}>
              {ingredient.name ? (
                <p className="text-sm font-bold uppercase tracking-wider text-ink-soft">
                  {ingredient.name}
                </p>
              ) : null}
            </div>
          );
        }

        // When swapped, the row shows the substitute; the swap line previews
        // whichever one is NOT showing, so clicking it always reads as "switch to…".
        const isSwapped = !!swapped[ingredient.id] && !!ingredient.substitute;
        const active = isSwapped ? ingredient.substitute! : ingredient;
        const inactive = isSwapped ? ingredient : ingredient.substitute;
        const qty = getQuantityString(active, scale, unitSystem, t);
        return (
          <div
            key={ingredient.id}
            className={`flex items-start gap-3 py-4 border-b border-line ${
              inGroup ? "pl-4" : ""
            }`}
          >
            <div className="max-w-32" style={{ minWidth: qtyColWidth || undefined }}>
              {!!qty && (
                <span
                  data-qty
                  className="inline-block whitespace-nowrap text-base font-bold text-terracotta"
                >
                  {qty}
                </span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-base font-semibold text-ink">{active.name}</p>
              {inactive ? (
                <button
                  type="button"
                  aria-label={t("recipe.swapSubstitute")}
                  onClick={() =>
                    setSwapped((prev) => ({ ...prev, [ingredient.id]: !prev[ingredient.id] }))
                  }
                  className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-terracotta hover:text-terracotta-dark cursor-pointer"
                >
                  <span aria-hidden>⇄</span>
                  {t("recipe.orSubstitute", {
                    substitute: [
                      getQuantityString(inactive, scale, unitSystem, t),
                      inactive.name,
                    ]
                      .filter(Boolean)
                      .join(" "),
                  })}
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default RecipeIngredientsView;
